import { useCallback, useEffect, useRef } from "react";
import { safeLocalStorage } from "@helpers/storage";

const PREFIX = "kick_event_";

interface UseWindowMessageOptions<T> {
    scope: string;
    handler: (message: T) => void;
}

interface WindowMessageEnvelope {
    key: string;
    payload: string;
}

function getEventKey(name: string) {
    return PREFIX + name;
}

function isWindowMessageEnvelope(
    data: MessageEvent["data"],
): data is WindowMessageEnvelope {
    return (
        typeof data === "object" &&
        data !== null &&
        typeof data.key === "string" &&
        data.key.startsWith(PREFIX) &&
        typeof data.payload === "string"
    );
}

export function sendWindowMessage(scope: string, message: any) {
    const key = getEventKey(scope);
    const payload = JSON.stringify(message);
    const envelope: WindowMessageEnvelope = { key, payload };
    window.opener?.postMessage(envelope, window.location.origin);
    safeLocalStorage.setItem(key, payload);
    setTimeout(() => safeLocalStorage.removeItem(key), 0);
}

export function useWindowMessage<T = any>({
    scope,
    handler,
}: UseWindowMessageOptions<T>): void {
    const handlerRef = useRef(handler);
    const lastPayloadRef = useRef<string | null>(null);

    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);

    const receiveMessage = useCallback(
        (key: string | null, payload: string | null) => {
            if (
                key !== getEventKey(scope) ||
                !payload ||
                payload === lastPayloadRef.current
            ) {
                return;
            }
            lastPayloadRef.current = payload;
            let message: T;
            try {
                message = JSON.parse(payload);
            } catch {
                return;
            }
            handlerRef.current(message);
        },
        [scope],
    );

    useEffect(() => {
        const onStorage = (event: StorageEvent) =>
            receiveMessage(event.key, event.newValue);
        const onMessage = (event: MessageEvent) => {
            if (
                event.origin === window.location.origin &&
                isWindowMessageEnvelope(event.data)
            ) {
                receiveMessage(event.data.key, event.data.payload);
            }
        };

        window.addEventListener("storage", onStorage, false);
        window.addEventListener("message", onMessage, false);

        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("message", onMessage);
        };
    }, [receiveMessage]);
}
